import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface FieldProps {
  label: string;
  /** Persistent guidance. Prefer this over a placeholder for anything required. */
  hint?: string;
  error?: string | undefined;
  required?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * Label + control + hint/error wrapper.
 *
 * Wires the accessibility relationships that are easy to omit and impossible to
 * notice without a screen reader: `aria-describedby` pointing at the hint AND the
 * error, `aria-invalid` on the control, and `role="alert"` on the error so it is
 * announced when it appears rather than only when focus reaches the field.
 */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldProps & { children: ReactNode }) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <span className="text-sm font-medium text-content">
        {label}
        {required === true && (
          <>
            {" "}
            <span className="text-danger" aria-hidden>
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
      </span>
      {children}
      {hint !== undefined && error === undefined && (
        <span className="text-xs text-content-muted">{hint}</span>
      )}
      {error !== undefined && (
        <span role="alert" className="text-xs font-medium text-danger">
          {error}
        </span>
      )}
    </div>
  );
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  hint?: string;
  error?: string | undefined;
  leadingIcon?: ReactNode;
  trailingSlot?: ReactNode;
}

/* Focus is a border-color change plus a soft same-hue ring, replacing the
   global outline (inputs match :focus-visible even on mouse focus, so leaving
   the outline on would double the indicator). */
const CONTROL_CLASSES =
  "w-full rounded-md border bg-card px-3 py-1.5 text-sm text-content " +
  "placeholder:text-content-muted " +
  "disabled:cursor-not-allowed disabled:bg-sunken disabled:opacity-70 " +
  "transition-colors focus:outline-none focus:ring-2";

const FOCUS_OK =
  "border-[var(--line-strong)] focus:border-[var(--ring-focus)] " +
  "focus:ring-[color-mix(in_oklab,var(--ring-focus)_20%,transparent)]";

const FOCUS_ERROR =
  "border-danger focus:border-danger " +
  "focus:ring-[color-mix(in_oklab,var(--color-danger)_20%,transparent)]";

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leadingIcon, trailingSlot, className, id, required, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;

  const control = (
    <div className="relative">
      {leadingIcon !== undefined && (
        <span
          aria-hidden
          className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-muted"
        >
          {leadingIcon}
        </span>
      )}
      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-invalid={error !== undefined || undefined}
        // Both ids, filtered: an aria-describedby pointing at a non-existent
        // element is silently dropped by some screen readers along with the rest
        // of the list.
        aria-describedby={
          [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
            .filter((value) => value !== null)
            .join(" ") || undefined
        }
        className={cn(
          CONTROL_CLASSES,
          "h-9",
          leadingIcon !== undefined && "pl-9",
          trailingSlot !== undefined && "pr-10",
          error !== undefined ? FOCUS_ERROR : FOCUS_OK,
          className,
        )}
        {...props}
      />
      {trailingSlot !== undefined && (
        <span className="absolute right-2 top-1/2 -translate-y-1/2">{trailingSlot}</span>
      )}
    </div>
  );

  if (label === undefined) return control;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-content">
        {label}
        {required === true && (
          <>
            {" "}
            <span className="text-danger" aria-hidden>
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
      </label>
      {control}
      {hint !== undefined && error === undefined && (
        <span id={hintId} className="text-xs text-content-muted">
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </span>
      )}
    </div>
  );
});

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string | undefined;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, required, rows = 4, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  const hintId = `${textareaId}-hint`;
  const errorId = `${textareaId}-error`;

  const control = (
    <textarea
      ref={ref}
      id={textareaId}
      rows={rows}
      required={required}
      aria-invalid={error !== undefined || undefined}
      aria-describedby={
        [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
          .filter((value) => value !== null)
          .join(" ") || undefined
      }
      className={cn(
        CONTROL_CLASSES,
        "resize-y py-2",
        error !== undefined ? FOCUS_ERROR : FOCUS_OK,
        className,
      )}
      {...props}
    />
  );

  if (label === undefined) return control;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={textareaId} className="text-sm font-medium text-content">
        {label}
        {required === true && (
          <>
            {" "}
            <span className="text-danger" aria-hidden>
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        )}
      </label>
      {control}
      {hint !== undefined && error === undefined && (
        <span id={hintId} className="text-xs text-content-muted">
          {hint}
        </span>
      )}
      {error !== undefined && (
        <span id={errorId} role="alert" className="text-xs font-medium text-danger">
          {error}
        </span>
      )}
    </div>
  );
});
